import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExportButtons, fmtQ } from "./exports";

const th = "px-2 py-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 whitespace-nowrap";
const td = "px-2 py-1.5 text-xs whitespace-nowrap";

// ST-01-FIX01 · Resolved document lineage (backend-validated; "Not Linked" when unproven).
const REF_COLS = [["source_document_type", "Source Type"], ["source_document_no", "Source Document"], ["po_no", "PO No"],
  ["wo_no", "WO No"], ["cwo_no", "CWO No"], ["order_type", "Order Type"], ["order_no", "SO / ESO No"], ["ps_no", "PS / EPS"]];

const RefCells = ({ x }) => REF_COLS.map(([k]) => (
  <td key={k} data-testid={`sr-ref-${k}`}
    className={`${td} ${String(x[k] || "").startsWith("Not Linked") ? "text-zinc-500 italic" : ""}`}>{x[k] || "—"}</td>
));

function TxnDetail({ t }) {
  const f = [["Movement", t.movement_id], ["Posted At", t.posted_at], ["Business Date", t.business_date],
    ["Stored Ref", t.stored_ref], ["Quality", t.quality_status], ["Notes", t.notes]];
  return (
    <div className="grid grid-cols-3 gap-x-4 gap-y-1 px-3 py-2 bg-[var(--bg-0)] text-[11px]" data-testid={`sr-txn-detail-${t.movement_id}`}>
      {f.map(([k, v]) => <div key={k}><span className="text-zinc-500">{k}: </span><span className="font-mono text-zinc-300 break-all">{v || "—"}</span></div>)}
    </div>
  );
}

function TxnTable({ txns, ledger }) {
  const [openId, setOpenId] = useState(null);
  const cols = ["Posting Date", "Type", ...(ledger ? ["Column"] : []), "Component", ...REF_COLS.map(c => c[1]),
    "Stored Ref", "Batch / Lot", "Qty", ...(ledger ? ["Balance"] : [])];
  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="sr-txn-table">
        <thead><tr className="border-b border-[var(--border)]">{cols.map(c => <th key={c} className={`${th} ${["Qty", "Balance"].includes(c) ? "text-right" : "text-left"}`}>{c}</th>)}</tr></thead>
        <tbody>{txns.map(t => (
          <Fragment key={t.movement_id}>
            <tr data-testid={`sr-txn-${t.movement_id}`} onClick={() => setOpenId(openId === t.movement_id ? null : t.movement_id)}
              className="border-b border-[var(--border)] cursor-pointer hover:bg-white/5 font-mono">
              <td className={td}>{t.posting_date}</td>
              <td className={`${td} ${t.is_reversal ? "text-amber-300" : ""}`}>{t.transaction_type}</td>
              {ledger && <td className={td}>{t.column}</td>}
              <td className={td}>{t.component || "—"}</td>
              <RefCells x={t} />
              <td className={`${td} text-zinc-400`}>{t.stored_ref || "—"}</td>
              <td className={td}>{t.batch_lot || "—"}</td>
              <td className={`${td} text-right tabular-nums ${t.quantity < 0 ? "text-amber-300" : ""}`}>{fmtQ(t.quantity)}</td>
              {ledger && <td className={`${td} text-right tabular-nums`}>{fmtQ(t.running_balance)}</td>}
            </tr>
            {openId === t.movement_id && <tr><td colSpan={cols.length}><TxnDetail t={t} /></td></tr>}
          </Fragment>
        ))}</tbody>
      </table>
    </div>
  );
}

function Groups({ d }) {
  const [open, setOpen] = useState(null);
  const cols = ["Link", "Reference", "Component", ...REF_COLS.map(c => c[1]), "Txns", "Qty"];
  return (
    <div className="overflow-x-auto border border-[var(--border)] rounded-sm" data-testid="sr-drill-groups">
      <table className="w-full">
        <thead><tr className="border-b border-[var(--border)]">{cols.map(c => <th key={c} className={`${th} ${["Txns", "Qty"].includes(c) ? "text-right" : "text-left"}`}>{c}</th>)}</tr></thead>
        <tbody>{d.groups.map((g, i) => (
          <Fragment key={i}>
            <tr data-testid={`sr-group-${i}`} onClick={() => setOpen(open === i ? null : i)}
              className="border-b border-[var(--border)] cursor-pointer hover:bg-white/5 font-mono">
              <td className={`${td} text-zinc-500`}>{g.link_type}</td>
              <td className={`${td} text-white`}>{g.reference}</td>
              <td className={`${td} text-sky-300`}>{g.component || "—"}</td>
              <RefCells x={g} />
              <td className={`${td} text-right text-zinc-500`}>{g.count}</td>
              <td className={`${td} text-right tabular-nums ${g.quantity < 0 ? "text-amber-300" : "text-white"}`}>{fmtQ(g.quantity)} {d.uom}</td>
            </tr>
            {open === i && <tr><td colSpan={cols.length} className="bg-[var(--bg-0)]"><TxnTable txns={g.transactions} /></td></tr>}
          </Fragment>
        ))}</tbody>
      </table>
      {d.groups.length === 0 && <div className="p-3 text-xs text-zinc-500">No transactions.</div>}
    </div>
  );
}

export function StockReportDrill({ target, params, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!target) return;
    setD(null);
    api.get("/inventory/v2/stock-report/drill", { params: { ...params, item_id: undefined, column: target.column, item: target.item, uom: target.uom } })
      .then(r => setD(r.data)).catch(e => { toast.error(e?.response?.data?.detail?.message || "Drill-down failed"); onClose(); });
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  const exportParams = target ? { ...params, column: target.column, item: target.item, uom: target.uom } : {};
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="sr-drill-dialog">
        <DialogHeader><DialogTitle className="font-display">{d ? `${d.column_label} · ${d.item_code}` : "Loading…"}</DialogTitle></DialogHeader>
        {d && (
          <div className="space-y-3 min-w-0">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs font-mono text-zinc-400">
                {d.godown.code} · {d.from_date} → {d.to_date} · Total <span data-testid="sr-drill-total" className="text-white">{fmtQ(d.total)}</span> {d.uom}
                {d.mode === "ledger" && <> · {d.start_label} {fmtQ(d.start_balance)} → {fmtQ(d.end_balance)}</>}
              </div>
              <ExportButtons params={exportParams} prefix="sr-drill" />
            </div>
            {d.mode === "movement" ? <Groups d={d} /> : <TxnTable txns={d.lines} ledger />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
